import React, { useEffect, useRef, useState, useCallback } from "react";
import * as d3 from "d3";
import { Table, Row, Col } from "antd";

const CentralClearingVisualization = () => {
  const svgRef = useRef();
  const [error, setError] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [payoutsData, setPayoutsData] = useState([]);

  const width = 1000;
  const height = 600;
  console.log("Graph data initialized:", !!graphData);

  const preprocessInfinity = (jsonString) => {
    return jsonString.replace(/Infinity/g, '"InfinityPlaceholder"');
  };

  const replaceInfinity = useCallback((obj) => {
    if (Array.isArray(obj)) {
      return obj.map(replaceInfinity);
    } else if (typeof obj === "object" && obj !== null) {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, replaceInfinity(value)])
      );
    } else if (obj === "InfinityPlaceholder") {
      return Infinity;
    }
    return obj;
  }, []);

  const processData = (finalGraph, clearingData) => {
    const nodes = new Set();
    const links = [];

    // Add MM and RM nodes from clearing data
    clearingData[0] &&
      Object.keys(clearingData[0]).forEach((mm) => nodes.add(mm));
    clearingData[1] &&
      Object.keys(clearingData[1]).forEach((rm) => nodes.add(rm));

    // Add CCP node
    nodes.add("CCP");

    // Create links from clearing data
    clearingData[0] &&
      Object.entries(clearingData[0]).forEach(([mm, data]) => {
        links.push({
          source: mm,
          target: "CCP",
          units: data.total_units,
          secondLegTotal: data.total_second_leg,
        });
      });

    clearingData[1] &&
      Object.entries(clearingData[1]).forEach(([rm, data]) => {
        links.push({
          source: "CCP",
          target: rm,
          units: data.total_units,
          secondLegTotal: data.total_second_leg,
        });
      });

    return { nodes: Array.from(nodes), links };
  };

  const createVisualization = useCallback((data) => {
    try {
      const svg = d3
        .select(svgRef.current)
        .attr("width", width)
        .attr("height", height)
        .attr("viewBox", [0, 0, width, height])
        .attr("style", "max-width: 100%; height: auto;");

      svg.selectAll("*").remove();

      // Arrow marker with uniform size
      svg
        .append("defs")
        .append("marker")
        .attr("id", "arrowhead")
        .attr("viewBox", "-0 -5 10 10")
        .attr("refX", 35) // Adjusted for larger CCP node
        .attr("refY", 0)
        .attr("orient", "auto")
        .attr("markerWidth", 4)
        .attr("markerHeight", 4)
        .append("svg:path")
        .attr("d", "M 0,-5 L 10 ,0 L 0,5")
        .attr("fill", "#666")
        .style("stroke", "none");

      const nodesArray = data.nodes.map((id) => {
        let nodeType = "other";
        if (id.startsWith("MM")) nodeType = "MM";
        else if (id.startsWith("RM")) nodeType = "RM";
        else if (id === "CCP") nodeType = "CCP";

        let x = width / 2;
        if (nodeType === "MM") x = width * 0.25;
        else if (nodeType === "CCP") x = width * 0.5;
        else if (nodeType === "RM") x = width * 0.75;

        return {
          id,
          type: nodeType,
          x,
          y: height / 2,
        };
      });

      const simulation = d3
        .forceSimulation(nodesArray)
        .force(
          "link",
          d3
            .forceLink(data.links)
            .id((d) => d.id)
            .distance(200)
        ) // Increased distance
        .force("charge", d3.forceManyBody().strength(-2000))
        .force(
          "collision",
          d3.forceCollide().radius((d) => (d.type === "CCP" ? 100 : 50))
        )
        .force(
          "x",
          d3
            .forceX()
            .x((d) => {
              if (d.type === "MM") return width * 0.25;
              if (d.type === "CCP") return width * 0.5;
              if (d.type === "RM") return width * 0.75;
              return d.x;
            })
            .strength(1)
        )
        .force("y", d3.forceY(height / 2).strength(0.1));

      // Create edges with uniform style
      const link = svg
        .append("g")
        .selectAll("line")
        .data(data.links)
        .join("line")
        .attr("class", "link")
        .attr("marker-end", "url(#arrowhead)")
        .style("stroke", "#666")
        .style("stroke-width", 2); // Uniform width

      // Edge labels
      const linkLabels = svg
        .append("g")
        .selectAll("text")
        .data(data.links)
        .join("text")
        .attr("class", "edge-label")
        .style("font-size", "12px")
        .style("text-anchor", "middle")
        .text((d) => `${d.units} T | $${d.secondLegTotal.toFixed(2)}`);

      const colorScale = d3
        .scaleOrdinal()
        .domain(["MM", "RM", "CCP"])
        .range(["#69b3a2", "#ff7f50", "#ffd700"]);

      // Create nodes
      const nodeGroup = svg
        .append("g")
        .selectAll("g")
        .data(nodesArray)
        .join("g")
        .call(drag(simulation));

      // Add different shapes based on node type
      nodeGroup.each(function (d) {
        const g = d3.select(this);
        if (d.type === "CCP") {
          // Larger CCP rectangle
          g.append("rect")
            .attr("width", 100) // Doubled width
            .attr("height", 120) // Tripled height
            .attr("x", -50) // Center the rectangle
            .attr("y", -60)
            .style("fill", colorScale(d.type))
            .style("stroke", "#fff")
            .style("stroke-width", 2);
        } else {
          g.append("circle")
            .attr("r", 20)
            .style("fill", colorScale(d.type))
            .style("stroke", "#fff")
            .style("stroke-width", 2);
        }
      });

      // Add labels
      nodeGroup
        .append("text")
        .text((d) => d.id)
        .attr("font-size", (d) => (d.type === "CCP" ? "18px" : "14px")) // Larger font for CCP
        .attr("font-weight", "bold")
        .attr("text-anchor", "middle")
        .attr("dy", ".35em")
        .style("fill", "#fff");

      simulation.on("tick", () => {
        // Constrain positions
        nodesArray.forEach((d) => {
          if (d.type === "MM") {
            d.x = Math.max(width * 0.2, Math.min(width * 0.3, d.x));
          } else if (d.type === "CCP") {
            d.x = width * 0.5;
          } else if (d.type === "RM") {
            d.x = Math.max(width * 0.7, Math.min(width * 0.8, d.x));
          }
          d.y = Math.max(60, Math.min(height - 60, d.y)); // Adjusted for larger CCP height
        });

        link
          .attr("x1", (d) => d.source.x)
          .attr("y1", (d) => d.source.y)
          .attr("x2", (d) => d.target.x)
          .attr("y2", (d) => d.target.y);

        linkLabels
          .attr("x", (d) => (d.source.x + d.target.x) / 2)
          .attr("y", (d) => (d.source.y + d.target.y) / 2 - 10);

        nodeGroup.attr("transform", (d) => `translate(${d.x},${d.y})`);
      });
    } catch (error) {
      console.error("Error creating visualization:", error);
      setError("Failed to create visualization: " + error.message);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch both datasets
        const [finalResponse, clearingResponse, payoutsResponse] =
          await Promise.all([
            fetch("https://api.repo.tradingmechanism.com/get_final"),
            fetch("https://api.repo.tradingmechanism.com/get_central_clearing"),
            fetch("https://api.repo.tradingmechanism.com/get_central_clearing_payouts"),
          ]);

        if (!finalResponse.ok || !clearingResponse.ok || !payoutsResponse.ok) {
          throw new Error("Failed to fetch data");
        }

        // Process final data
        const finalText = await finalResponse.text();
        const preprocessedFinal = preprocessInfinity(finalText);
        const finalData = replaceInfinity(JSON.parse(preprocessedFinal));

        // Process clearing data
        const clearingData = await clearingResponse.json();

        // Process payouts data
        const payoutsData = await payoutsResponse.json();
        const formattedPayoutsData = Object.keys(payoutsData).map(
          (node, index) => ({
            key: index,
            node,
            payout:
              payoutsData[node] > 0
                ? `+$${payoutsData[node]}`
                : `-$${Math.abs(payoutsData[node])}`,
          })
        );
        setPayoutsData(formattedPayoutsData);

        // Combine the data
        const combinedData = processData(
          finalData.graph_states[0],
          clearingData
        );
        setGraphData(combinedData);
        createVisualization(combinedData);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error.message);
      }
    };

    fetchData();
  }, [createVisualization, replaceInfinity]);

  const drag = (simulation) => {
    function dragstarted(event) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }

    function dragged(event) {
      const node = event.subject;
      if (node.type === "MM") {
        event.subject.fx = Math.max(
          width * 0.2,
          Math.min(width * 0.3, event.x)
        );
      } else if (node.type === "CCP") {
        event.subject.fx = width * 0.5;
      } else if (node.type === "RM") {
        event.subject.fx = Math.max(
          width * 0.7,
          Math.min(width * 0.8, event.x)
        );
      }
      event.subject.fy = event.y;
    }

    function dragended(event) {
      if (!event.active) simulation.alphaTarget(0);
      if (event.subject.type !== "CCP") {
        event.subject.fx = null;
        event.subject.fy = null;
      }
    }

    return d3
      .drag()
      .on("start", dragstarted)
      .on("drag", dragged)
      .on("end", dragended);
  };

  const columns = [
    {
      title: "Node",
      dataIndex: "node",
      key: "node",
    },
    {
      title: "Payout to CCP",
      dataIndex: "payout",
      key: "payout",
    },
  ];

  return (
    <Row gutter={16} align="top">
      <Col span={16}>
        <div>
          <svg ref={svgRef}></svg>
          {error && <div className="text-red-500">Error: {error}</div>}
        </div>
      </Col>
      <Col span={8}>
        <Table
          dataSource={payoutsData}
          columns={columns}
          pagination={{ pageSize: 20 }}
          size="small"
          style={{ fontSize: "0.7em" }} // Add paddingTop
          rowClassName="compact-row"
        />
      </Col>
    </Row>
  );
};

export default CentralClearingVisualization;
